import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Users, Flame, Sparkles } from "lucide-react";
import PageSeo from "@/components/PageSeo";
import { api } from "@/lib/api";
import { SITE } from "@/lib/site";

const pillars = [
  { icon: Sparkles, t: "Weddings", d: "Ceremonies on the lawn, receptions on the veranda, cake on the deck." },
  { icon: Users, t: "Private Dining", d: "Book the veranda or the arch room — 12 to 60 guests, plated or family-style." },
  { icon: Flame, t: "Bonfire Nights", d: "A fire pit, a live set, a short bar list — Friday and Saturday, seasonal." },
];

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="events-page" className="pt-32 pb-24">
      <PageSeo
        title="Events — Weddings, Bonfire Nights & Private Functions"
        description="Lawn weddings, veranda dinners, Sunday long lunches and Friday-Saturday bonfire nights at The Barracks Bistro. Enquire early — dates fill fast."
        path="/events"
      />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="eyebrow">Events & Private Hire</div>
        <h1 className="mt-3 text-5xl sm:text-6xl max-w-3xl leading-[1]">
          The lawn holds two hundred. <br />
          <span className="italic text-[hsl(var(--brass))]">The kitchen holds its own.</span>
        </h1>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="aspect-[4/5] overflow-hidden">
            <img src="/images/events-1.jpg" alt="Lawn wedding setup" className="w-full h-full object-cover" data-testid="events-img-1" />
          </div>
          <div className="aspect-[4/5] overflow-hidden">
            <img src="/images/events-2.jpg" alt="Bonfire evening at TBB" className="w-full h-full object-cover" data-testid="events-img-2" />
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
          {pillars.map((e) => (
            <div key={e.t} className="border-t border-[hsl(var(--rule))] pt-6">
              <e.icon size={22} className="text-[hsl(var(--brass))]" />
              <h3 className="mt-4 text-2xl">{e.t}</h3>
              <p className="mt-3 text-[hsl(var(--ink-soft))]">{e.d}</p>
            </div>
          ))}
        </div>

        {/* Live events feed */}
        {events.length > 0 && (
          <div className="mt-20" data-testid="events-feed">
            <div className="eyebrow">What's on</div>
            <h2 className="mt-3 text-3xl sm:text-4xl">Coming up at the bistro</h2>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map((ev) => (
                <article key={ev.id} data-testid={`event-card-${ev.id}`} className="bg-[hsl(var(--bg-2))] border border-[hsl(var(--rule))] overflow-hidden flex flex-col">
                  {ev.image_url && (
                    <div className="aspect-[4/3] overflow-hidden">
                      <img src={ev.image_url} alt={ev.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="eyebrow">{ev.date}</div>
                    <h3 className="mt-2 text-xl">{ev.title}</h3>
                    <p className="mt-2 text-sm text-[hsl(var(--ink-soft))] flex-1">{ev.description}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        <div className="mt-16 bg-[hsl(var(--bg-2))] p-10 md:p-14">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-3xl">Enquire about your event</h2>
              <p className="mt-3 text-[hsl(var(--ink-soft))]">
                Tell us the date, the count, and the vibe. We'll come back within a day with a
                proposal, menu options, and a costing.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="btn-brass justify-center" data-testid="events-whatsapp-btn">
                WhatsApp {SITE.phonePrimary} <ArrowRight size={16} />
              </a>
              <a href={`mailto:${SITE.email}?subject=Event enquiry`} className="btn-ghost justify-center" data-testid="events-email-btn">
                Email {SITE.email}
              </a>
              <Link to="/reservations" className="text-sm text-[hsl(var(--forest))] font-semibold underline underline-offset-4">
                Or send a form-based enquiry →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
